import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchCurrentUserBookings } from '../lib/bookingService';
import { isBookingEventLive, isCorporateBooking } from '../lib/bookingStage';
import {
  buildLiveDashboardEntries,
  buildTrendChartPoints,
  fetchClientLiveEventAnalytics,
  fetchLinkedTicketingEvents,
  formatDashboardDate,
  formatDashboardDateTime,
  trendBarHeightPx,
} from '../lib/liveEventDashboard';
import { supabase } from '../lib/supabaseClient';

const displayValue = (value, fallback = '—') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [recentBookings, setRecentBookings] = useState([]);
  const [ownerEmail, setOwnerEmail] = useState('');

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.bookingId === String(selectedBookingId)) || null,
    [entries, selectedBookingId]
  );

  const trendPoints = useMemo(
    () => buildTrendChartPoints(analytics?.trend_7d || []),
    [analytics]
  );

  const maxTrend = useMemo(
    () => Math.max(1, ...trendPoints.map((point) => point.count)),
    [trendPoints]
  );

  const loadLiveEntries = useCallback(async ({ preserveSelection = true } = {}) => {
    const { email, bookings } = await fetchCurrentUserBookings();
    setOwnerEmail(email || '');
    setRecentBookings((bookings || []).slice(0, 3));

    const liveOwned = (bookings || []).filter((booking) => isBookingEventLive(booking));
    const concertCodes = liveOwned
      .filter((booking) => !isCorporateBooking(booking))
      .map((booking) => booking.request_code)
      .filter(Boolean);

    const linkedEvents = await fetchLinkedTicketingEvents(concertCodes);
    const nextEntries = buildLiveDashboardEntries(liveOwned, linkedEvents);
    setEntries(nextEntries);

    setSelectedBookingId((previous) => {
      if (preserveSelection && previous && nextEntries.some((entry) => entry.bookingId === String(previous))) {
        return String(previous);
      }
      return nextEntries[0]?.bookingId || '';
    });

    return nextEntries;
  }, []);

  const loadAnalytics = useCallback(async (bookingId) => {
    if (!bookingId) {
      setAnalytics(null);
      setAnalyticsError('');
      return;
    }

    setAnalyticsLoading(true);
    setAnalyticsError('');
    const result = await fetchClientLiveEventAnalytics(bookingId);
    if (!result?.ok || result.kind !== 'ticketed') {
      setAnalytics(null);
      setAnalyticsError(result?.message || 'Unable to load ticket analytics.');
      setAnalyticsLoading(false);
      return;
    }

    setAnalytics(result);
    setAnalyticsLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        await loadLiveEntries({ preserveSelection: false });
      } catch (error) {
        console.error('Failed to load live dashboard entries:', error);
        if (!cancelled) {
          setEntries([]);
          setSelectedBookingId('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadLiveEntries]);

  useEffect(() => {
    if (!selectedEntry) {
      setAnalytics(null);
      setAnalyticsError('');
      return;
    }

    if (selectedEntry.kind !== 'ticketed') {
      setAnalytics(null);
      setAnalyticsError('');
      return;
    }

    loadAnalytics(selectedEntry.bookingId);
  }, [selectedEntry, loadAnalytics]);

  useEffect(() => {
    if (!ownerEmail) return undefined;

    const listChannel = supabase
      .channel(`dashboard-live-list:${ownerEmail}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `requester_email=eq.${ownerEmail}`,
        },
        () => {
          loadLiveEntries({ preserveSelection: true }).catch((error) => {
            console.error('Failed to refresh live dashboard list:', error);
          });
        }
      )
      .subscribe();

    return () => {
      listChannel.unsubscribe();
    };
  }, [ownerEmail, loadLiveEntries]);

  useEffect(() => {
    if (!selectedEntry?.bookingId) return undefined;

    const bookingChannel = supabase
      .channel(`dashboard-booking:${selectedEntry.bookingId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'bookings',
          filter: `id=eq.${selectedEntry.bookingId}`,
        },
        () => {
          loadLiveEntries({ preserveSelection: true }).catch((error) => {
            console.error('Failed to refresh selected booking:', error);
          });
        }
      )
      .subscribe();

    return () => {
      bookingChannel.unsubscribe();
    };
  }, [selectedEntry?.bookingId, loadLiveEntries]);

  useEffect(() => {
    if (selectedEntry?.kind !== 'ticketed' || !selectedEntry?.linkedEvent?.id) return undefined;

    const eventId = selectedEntry.linkedEvent.id;
    const bookingId = selectedEntry.bookingId;

    const refresh = () => {
      loadAnalytics(bookingId);
    };

    const eventChannel = supabase
      .channel(`dashboard-event:${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: `id=eq.${eventId}`,
        },
        refresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ticket_tiers',
          filter: `event_id=eq.${eventId}`,
        },
        refresh
      )
      .subscribe();

    const pollId = window.setInterval(refresh, 20000);

    return () => {
      window.clearInterval(pollId);
      eventChannel.unsubscribe();
    };
  }, [selectedEntry?.kind, selectedEntry?.linkedEvent?.id, selectedEntry?.bookingId, loadAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mx-auto mb-4" />
          <p className="text-slate-300">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="min-h-[calc(100vh-8rem)] text-white">
        <div className="mx-auto w-full max-w-md lg:max-w-6xl space-y-6 lg:space-y-8 pb-8 lg:pb-0">
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">Event Dashboard</h1>

          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 flex items-start gap-4">
            <AlertCircle size={24} className="text-amber-400 shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-bold text-amber-300 mb-2">No Live Events Yet</h3>
              <p className="text-amber-200/80 text-sm mb-4">
                Your personal dashboard appears only for bookings that have reached Event is Now Live!
              </p>
              <div className="space-y-2 text-sm text-amber-100/60">
                <p>Concert events appear here after they are published on the ticketing website.</p>
                <p>Corporate events appear here once they are marked live in the booking workflow.</p>
              </div>
              <Link
                to="/book"
                className="inline-block mt-6 px-4 py-2 bg-brand-maroon hover:bg-brand-red text-white font-semibold rounded-lg transition"
              >
                Go to Bookings
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const booking = selectedEntry?.booking;

  return (
    <div className="min-h-[calc(100vh-8rem)] text-white">
      <div className="mx-auto w-full max-w-md lg:max-w-6xl space-y-6 lg:space-y-8 pb-8 lg:pb-0">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white">Event Dashboard</h1>

        <select
          value={selectedBookingId || ''}
          onChange={(event) => setSelectedBookingId(String(event.target.value))}
          className="rounded-md bg-[#8b0000] px-4 py-3 text-sm font-bold text-white w-full lg:w-auto lg:min-w-[320px] cursor-pointer hover:bg-[#a00000] transition"
        >
          {entries.map((entry) => (
            <option key={entry.bookingId} value={entry.bookingId}>
              {entry.label}
            </option>
          ))}
        </select>

        {selectedEntry?.kind === 'corporate' && booking ? (
          <section className="space-y-5">
            <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Corporate Event</p>
              <h2 className="mt-2 text-2xl font-black text-white">{displayValue(booking.event_name, 'Untitled Event')}</h2>
              <p className="mt-2 text-sm text-emerald-300">Event is Now Live!</p>
            </article>

            <div className="grid gap-5 lg:grid-cols-2">
              <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5 space-y-3">
                <h3 className="text-base font-bold text-white">Event Information</h3>
                <p className="text-sm text-slate-300"><span className="text-slate-500">Type:</span> {displayValue(booking.event_type)}</p>
                <p className="text-sm text-slate-300"><span className="text-slate-500">Theme:</span> {displayValue(booking.theme)}</p>
                <p className="text-sm text-slate-300"><span className="text-slate-500">Reference:</span> {displayValue(booking.request_code)}</p>
              </article>

              <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5 space-y-3">
                <h3 className="text-base font-bold text-white">Schedule & Location</h3>
                <p className="text-sm text-slate-300"><span className="text-slate-500">Date:</span> {formatDashboardDate(booking.event_date)}</p>
                <p className="text-sm text-slate-300"><span className="text-slate-500">Venue:</span> {displayValue(booking.venue, 'To be confirmed')}</p>
                <p className="text-sm text-slate-300"><span className="text-slate-500">Attendees:</span> {displayValue(booking.attendee_count, 'Not specified')}</p>
              </article>
            </div>

            <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5 space-y-3">
              <h3 className="text-base font-bold text-white">Booking Details</h3>
              <p className="text-sm text-slate-300"><span className="text-slate-500">Budget range:</span> {displayValue(booking.budget_range)}</p>
              <p className="text-sm text-slate-300 whitespace-pre-wrap"><span className="text-slate-500">Details:</span> {displayValue(booking.details || booking.specific_needs, 'No additional details')}</p>
              <p className="text-xs text-slate-500">Updated {formatDashboardDateTime(booking.updated_at || booking.created_at)}</p>
              <Link
                to={`/book/progress/${booking.id}`}
                className="inline-flex mt-2 text-sm font-semibold text-brand-gold hover:underline"
              >
                Open Booking Progress
              </Link>
            </article>
          </section>
        ) : null}

        {selectedEntry?.kind === 'ticketed' ? (
          <section className="space-y-5">
            <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
              <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5">
                <p className="text-sm font-semibold text-slate-200">Ticket Sales Summary</p>
                {analyticsLoading ? (
                  <p className="mt-4 text-sm text-slate-400">Loading ticket analytics...</p>
                ) : analyticsError ? (
                  <p className="mt-4 text-sm text-rose-300">{analyticsError}</p>
                ) : (
                  <div className="mt-4 space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-slate-400">Sold</p>
                        <p className="text-2xl font-black text-white">{analytics?.sold ?? 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Remaining</p>
                        <p className="text-2xl font-black text-white">{analytics?.remaining ?? 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Capacity</p>
                        <p className="text-2xl font-black text-white">{analytics?.capacity ?? 0}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-slate-300">
                        <span>Sales progress</span>
                        <span>{analytics?.progress_percent ?? 0}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-white/90"
                          style={{ width: `${Math.min(100, Number(analytics?.progress_percent || 0))}%` }}
                        />
                      </div>
                    </div>
                    <p className="text-xs text-emerald-300">
                      Live status: {displayValue(analytics?.event?.status, 'published')}
                    </p>
                  </div>
                )}
              </article>

              <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-200">Ticket Sales Trend</p>
                    <p className="text-xs text-slate-400 mt-1">Last 7 days</p>
                  </div>
                </div>
                <div className="mt-5 rounded-2xl border border-white/10 bg-black/20 p-4">
                  <div className="flex h-36 items-end gap-2">
                    {trendPoints.map((point) => {
                      const barHeight = trendBarHeightPx(point.count, maxTrend, 112);
                      return (
                        <div key={point.day} className="flex-1 h-full flex flex-col items-center justify-end gap-2 min-w-0">
                          {point.count > 0 ? (
                            <span className="text-[10px] font-semibold text-slate-200 leading-none">
                              {point.count}
                            </span>
                          ) : (
                            <span className="text-[10px] leading-none text-transparent">0</span>
                          )}
                          <div className="w-full flex-1 flex items-end">
                            <div
                              className="w-full rounded-t-md bg-[linear-gradient(180deg,rgba(78,113,255,0.95),rgba(198,208,255,0.9))]"
                              style={{ height: `${barHeight}px` }}
                              title={`${point.count} tickets`}
                            />
                          </div>
                          <div className="text-[10px] leading-none text-slate-400 text-center">
                            <div>{point.label.split(' ')[0]}</div>
                            <div>{point.label.split(' ')[1]}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </article>
            </div>

            <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5">
              <h2 className="text-base font-bold text-white">Ticket Tier Performance</h2>
              <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
                <div className="grid grid-cols-[1.2fr_0.7fr_0.7fr_0.7fr] bg-white/5 px-4 py-3 text-xs font-medium text-slate-300">
                  <div>Tier Name</div>
                  <div className="text-center">Sold</div>
                  <div className="text-center">Remaining</div>
                  <div className="text-right">Capacity</div>
                </div>
                {(analytics?.tiers || []).length === 0 ? (
                  <div className="px-4 py-6 text-sm text-slate-400">No ticket tiers yet.</div>
                ) : (
                  (analytics?.tiers || []).map((tier) => (
                    <div
                      key={tier.tier_id || tier.name}
                      className="grid grid-cols-[1.2fr_0.7fr_0.7fr_0.7fr] px-4 py-3 text-sm border-t border-white/10"
                    >
                      <div className="text-white font-medium">{tier.name}</div>
                      <div className="text-center text-slate-300">{tier.sold}</div>
                      <div className="text-center text-slate-300">{tier.remaining}</div>
                      <div className="text-right text-slate-200">{tier.capacity}</div>
                    </div>
                  ))
                )}
              </div>
            </article>

            <article className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5">
              <h2 className="text-base font-bold text-white mb-4">Recent Ticket Sales</h2>
              <div className="space-y-3">
                {(analytics?.recent_sales || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No recent ticket sales yet.</p>
                ) : (
                  (analytics?.recent_sales || []).map((sale, index) => (
                    <div
                      key={`${sale.created_at}-${sale.tier_name}-${index}`}
                      className="rounded-xl border border-white/10 bg-black/20 p-3 flex items-center justify-between gap-3"
                    >
                      <div>
                        <p className="text-sm font-semibold text-white">{sale.tier_name}</p>
                        <p className="text-xs text-slate-400">{formatDashboardDateTime(sale.created_at)}</p>
                      </div>
                      <p className="text-sm font-bold text-slate-200">{sale.quantity} ticket{sale.quantity === 1 ? '' : 's'}</p>
                    </div>
                  ))
                )}
              </div>
            </article>
          </section>
        ) : null}

        <section className="panel rounded-2xl border border-white/15 bg-white/[0.04] p-4 lg:p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-white">My Recent Bookings</h2>
            <Link to="/book" className="text-xs text-slate-400 hover:text-white">View all</Link>
          </div>
          <div className="space-y-3">
            {recentBookings.length === 0 ? (
              <p className="text-sm text-slate-400">No bookings yet.</p>
            ) : (
              recentBookings.map((item) => (
                <Link
                  key={item.id}
                  to={`/book/progress/${item.id}`}
                  className="block rounded-xl border border-white/10 bg-black/20 p-3 hover:border-white/30 transition"
                >
                  <p className="text-sm font-semibold text-white">{item.event_name || 'Untitled booking'}</p>
                  <p className="text-xs text-slate-400">{formatDashboardDateTime(item.updated_at || item.created_at)}</p>
                </Link>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
